import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import { FormLabel, Radio, RadioGroup } from '@mui/material';
import { useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';

type OptionId = string;

interface OptionMap<T> {
  readonly [id: OptionId]: T;
}

type Props<T> = {
  readonly label: string;
  readonly options: T[];
  readonly selectOptionId?: (object: T) => OptionId;
  readonly selectOptionLabel?: (object: T) => string;
  readonly defaultOptionId: OptionId;
  readonly onChange: (object: T) => void;
};

const defaultOptionIdSelector = (object: any) => object.id;
const defaultOptionLabelSelector = (object: any) => object.label;

const RadioButtons = <T extends object>(props: Props<T>) => {
  const {
    label,
    options,
    selectOptionId = defaultOptionIdSelector,
    selectOptionLabel = defaultOptionLabelSelector,
    defaultOptionId,
    onChange,
  } = props;

  const [selectedOptionId, setSelectedOptionId] = useState<OptionId | undefined>(defaultOptionId);

  const optionMap: OptionMap<T> = options
    .reduce((acc, option) => {
      const optionId = selectOptionId(option);

      return {
        ...acc,
        [optionId]: option,
      };
    }, {} as OptionMap<T>);

  const handleChange = (event: SelectChangeEvent) => {
    const optionId = event.target.value as OptionId;

    setSelectedOptionId(optionId);
    onChange(optionMap[optionId]);
  };

  const radioOptions = options.map(option => ({
    id: selectOptionId(option),
    label: selectOptionLabel(option),
  }));

  return (
    <FormControl>
      <FormLabel id="demo-radio-buttons-group-label">{label}</FormLabel>
      <RadioGroup
        aria-labelledby="demo-radio-buttons-group-label"
        name="radio-buttons-group"
        value={selectedOptionId}
        onChange={handleChange}
      >
        { radioOptions.map(radioOpt => (
          <FormControlLabel
            key={radioOpt.id}
            value={radioOpt.id}
            control={<Radio />}
            label={radioOpt.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

export default RadioButtons;
